import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'debounce';

import { IAppState } from 'config/redux/reducers';
import { IExpress } from 'pages/Checkout/types/express.types';
import { expressGetEnable as expressGetEnableAction } from 'pages/Checkout/redux/actions/express.actions';

import useDeliverySlot from 'common/DeliverySlot/hooks/useDeliverySlot.hook';
import { isExisty } from 'utils/helpers.util';
import { validateCartEntries } from 'pages/Checkout/redux/actions/validateCart.actions';

const toggleExpressDebounce = debounce((method: any, param: any) => method(param), 200);

const useExpressEnabled = (
  expressEnable: boolean
): [
  IExpress,
  boolean,
  {
    toggleExpressHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
    enableDisableExpressHandler: (flag: boolean) => void;
  }
] => {
  const [{ selectedSlot }] = useDeliverySlot();
  const dispatch = useDispatch();
  const {
    checkout: {
      shipment: { express: expressState },
      delivery: {
        deliverTo: { value },
      },
    },
  } = useSelector((state: IAppState) => state);
  const [expressEnableLoc, setExpressEnableLoc] = useState(expressEnable);

  useEffect(() => {
    if (expressEnable !== expressEnableLoc) {
      setExpressEnableLoc(expressEnable);
    }
  }, [expressEnable, expressEnableLoc]);

  const toggleExpress = useCallback(
    (flag: boolean) => {
      if (flag !== expressEnable) {
        dispatch(
          validateCartEntries(value as any, {
            isExpressCartValidate: true,
            expressEnable: flag,
            successCallback: () => {
              dispatch(expressGetEnableAction(flag, !isExisty(selectedSlot) && !flag));
            },
          }) as any
        );
      }
    },
    [dispatch, expressEnable, selectedSlot, value]
  );

  const toggleExpressHandler = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setExpressEnableLoc(checked);
    toggleExpressDebounce(toggleExpress, checked);
  };

  const enableDisableExpressHandler = (checked: boolean) => {
    setExpressEnableLoc(checked);
    toggleExpressDebounce(toggleExpress, checked);
  };

  useEffect(() => {
    // In case or error revert the local state
    if (!expressState.loading && expressState.error) {
      setExpressEnableLoc(expressEnable);
    }
  }, [expressState, expressEnable]);

  return [expressState, expressEnableLoc, { toggleExpressHandler, enableDisableExpressHandler }];
};

export default useExpressEnabled;
